import axios from './axiosInstance';
import axiosLib, { CancelToken } from 'axios';
import { FormResponse } from '../../models/form/FormResponse';
import CONSTANTS from '../../constants';
import { APIFormResponse, PaginatedResponse } from '../../types';

interface SportAreaParams {
  sport: string;
  areaId: string;
}

interface GetFormResponsesParams extends SportAreaParams {
  day?: number;
  startDate?: string;
  endDate?: string;
  formTypes?: string | string[];
  excludeFormTypes?: string | string[];
  formCategories?: string | string[];
  featureId?: string;
  trailId?: string;
  spatialFeatureId?: string;
  authorIds?: string[];
  limit?: number;
  pageCursor?: string;
  cancelToken?: CancelToken;
}

interface GetFormResponsesExportParams extends SportAreaParams {
  formResponseId?: string;
  day?: number;
  startDate?: string;
  endDate?: string;
  formTypes?: string | string[];
  featureId?: string;
  trailId?: string;
  spatialFeatureId?: string;
}

interface GetFormResponseParams extends SportAreaParams {
  formResponseId: string;
}

interface GetCsvParams extends SportAreaParams {
  formResponseId: string;
  fileName: string;
}

interface GetJumpMeasurementExportParams extends SportAreaParams {
  formResponseId: string;
  fileName: string;
}

interface FormResponseMutationParams extends SportAreaParams {
  formResponse: any;
}

interface MutationOptions {
  requestId?: string | null;
}

interface PutFormResponseImageParams extends SportAreaParams {
  formResponseId: string;
  questionSetIndex: number;
  questionIndex: number;
  file: File;
}

interface OrchestrateUploadParams extends SportAreaParams {
  formResponse: any;
  createOrUpdate: string;
}

interface DeleteFormResponseParams extends SportAreaParams {
  formResponseId: string;
}

interface GetFormResponsesExportReportSummaryParams extends SportAreaParams {
  startDate?: string;
  endDate?: string;
  fileName: string;
}

interface GetFormResponsesCountParams extends SportAreaParams {
  day?: number;
  startDate?: string;
  endDate?: string;
  formTypes?: string | string[];
  excludeFormTypes?: string | string[];
  formCategories?: string | string[];
  featureId?: string;
  trailId?: string;
  spatialFeatureId?: string;
}

const getFormResponses = ({sport, areaId, day, startDate, endDate, formTypes, excludeFormTypes, formCategories, authorIds, featureId, trailId, spatialFeatureId, limit, pageCursor, cancelToken}: GetFormResponsesParams): Promise<PaginatedResponse<APIFormResponse> | void> => {
  let url = `/${sport}/areas/${areaId}/formresponses`;
  // add query parameters
  const queryParameters: string[] = [];
  if (day) {
    queryParameters.push(`day=${day}`);
  } else if (startDate && endDate) {
    queryParameters.push(`startDate=${startDate}`);
    queryParameters.push(`endDate=${endDate}`);
  }
  if (formTypes) {
    queryParameters.push(`formTypes=${Array.isArray(formTypes) ? formTypes.join(',') : formTypes}`);
  }
  if (excludeFormTypes) {
    queryParameters.push(`excludeFormTypes=${Array.isArray(excludeFormTypes) ? excludeFormTypes.join(',') : excludeFormTypes}`);
  }
  if (formCategories) {
    queryParameters.push(`formCategories=${Array.isArray(formCategories) ? formCategories.join(',') : formCategories}`);
  }
  if (authorIds && authorIds.length) {
    queryParameters.push(`authorIds=${authorIds.join(',')}`);
  }
  if (featureId) {
    queryParameters.push(`featureId=${featureId}`);
  }
  if (trailId) {
    queryParameters.push(`trailId=${trailId}`);
  }
  if (spatialFeatureId) {
    queryParameters.push(`spatialFeatureId=${spatialFeatureId}`);
  }
  if (limit) {
    queryParameters.push(`limit=${limit}`);
  }
  if (pageCursor) {
    queryParameters.push(`pageCursor=${pageCursor}`);
  }
  if (queryParameters && queryParameters.length) {
    url += `?${queryParameters.join('&')}`;
  }
  return axios.get(url, { cancelToken })
    .then((response) => {
      const responseBody = response.data || {};
      return responseBody;
    })
    .catch((error) => {
      if (axiosLib.isCancel(error)) {
        // Handle cancellation (we'll let this propagate up)
        throw error;
      }
      console.log(error);
    });
};

const getFormResponsesExport = ({sport, areaId, formResponseId, day, startDate, endDate, formTypes, featureId, trailId, spatialFeatureId}: GetFormResponsesExportParams): Promise<any> => {
  let url = `/${sport}/areas/${areaId}/formresponses/export`;
  // add query parameters
  const queryParams: Record<string, string> = {};
  if (formResponseId) {
    queryParams.id = formResponseId;
  }
  if (day) {
    queryParams.day = String(day);
  } else if (startDate && endDate) {
    queryParams.startDate = startDate;
    queryParams.endDate = endDate;
  }
  if (formTypes) {
    queryParams.formTypes = Array.isArray(formTypes) ? formTypes.join(',') : formTypes;
  }
  if (featureId) {
    queryParams.featureId = featureId;
  }
  if (trailId) {
    queryParams.trailId = trailId;
  }
  if (spatialFeatureId) {
    queryParams.spatialFeatureId = spatialFeatureId;
  }
  return axios.get(url, {
    params: queryParams,
  })
    .then((response) => {
      const responseBody = response.data;
      return responseBody;
    });
};

const getFormResponse = ({ sport, areaId, formResponseId }: GetFormResponseParams): Promise<APIFormResponse | void> => {
  let url = `/${sport}/areas/${areaId}/formresponses/${formResponseId}`;
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      // The page tells "no access" (403) from "not found" (404) from a failure.
      throw error;
    });
};

const getFormResponseExport = ({ sport, areaId, formResponseId }: GetFormResponseParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/formresponses/${formResponseId}/export`;
  return axios.get(url, {
    responseType: 'arraybuffer',
    headers: {
      'Accept': 'application/zip'
    }
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ullr-export.zip`);
      document.body.appendChild(link);
      link.click();
    })
    .catch((error) => {
      console.log(error);
    });
};

const getCsv = ({ sport, areaId, formResponseId, fileName }: GetCsvParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/formresponses/${formResponseId}/csv`;
  return axios.get(url)
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.csv`); //or any other extension
      document.body.appendChild(link);
      link.click();
    });
};

const getJumpMeasurementExport = ({ sport, areaId, formResponseId, fileName }: GetJumpMeasurementExportParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/formresponses/${formResponseId}/jumpmeasurementexport`;
  return axios.get(url, {
    responseType: 'arraybuffer',
    headers: {
      'Accept': 'application/zip'
    }
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.zip`); //or any other extension
      document.body.appendChild(link);
      link.click();
    })
};

const putFormResponse = ({sport, areaId, formResponse}: FormResponseMutationParams, {requestId}: MutationOptions): Promise<any> => {
  /**
   * Copy the completed form so that we can manipulate it without modifying the original.
  */
  const completedForm = formResponse.completedForm;
  const completedFormCopy = JSON.parse(JSON.stringify(completedForm));

  /**
   * Remove the value for any question with questionType === 'images'.
   * Because these values are files, they'll get uploaded in separate requests.
   */
  for (const questionSet of completedFormCopy.questionSets) {
    for (const question of questionSet.questions) {
      if (question.questionType === 'images') {
        question.value = null;
      }
    }
  }

  const url = `/${sport}/areas/${areaId}/formresponses`;
  return axios.put(url, {
    ...formResponse,
    completedForm: completedFormCopy,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    }
  })
    .then((response) => {
      const savedFormResponse = response.data;
      return FormResponse.factory(savedFormResponse);
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const postFormResponse = ({sport, areaId, formResponse}: FormResponseMutationParams, {requestId}: MutationOptions): Promise<any> => {
  /**
   * Copy the completed form so that we can manipulate it without modifying the original.
  */
  const completedForm = formResponse.completedForm;
  const completedFormCopy = JSON.parse(JSON.stringify(completedForm));

  /**
   * Remove the value for any question with questionType === 'images'.
   * Because these values are files, they'll get uploaded in separate requests.
   */
  for (const questionSet of completedFormCopy.questionSets) {
    for (const question of questionSet.questions) {
      if (question.questionType === 'images') {
        question.value = null;
      }
    }
  }

  const url = `/${sport}/areas/${areaId}/formresponses/${formResponse.id}`;
  return axios.post(url, {
    ...formResponse,
    timestamp: formResponse.timestamp,
    completedForm: completedFormCopy,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    }
  })
    .then((response) => {
      const savedFormResponse = response.data;
      return FormResponse.factory(savedFormResponse);
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const putFormResponseImage = async (
  { sport, areaId, formResponseId, questionSetIndex, questionIndex, file }: PutFormResponseImageParams,
  { requestId }: MutationOptions,
): Promise<void> => {
  const base64Data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve((reader.result as string).split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const url = `/${sport}/areas/${areaId}/formresponses/${formResponseId}/images`;
  return axios.put(url, {
    questionSetIndex,
    questionIndex,
    base64File: base64Data,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    }
  })
    .then(() => {})
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const orchestrateUpload = async ({ sport, areaId, formResponse, createOrUpdate }: OrchestrateUploadParams): Promise<APIFormResponse | void> => {
  /**
   * Determine whether there are any `images` questions in this form response.
   * If there are, they must be uploaded separately in subsequent requests
   */
  const imageUploads: PutFormResponseImageParams[] = [];
  const completedForm = formResponse.completedForm;
  // iterate through questionSets and questions
  for (let qsIndex = 0; qsIndex < completedForm.questionSets.length; qsIndex++) {
    const questionSet = completedForm.questionSets[qsIndex];
    for (let qIndex = 0; qIndex < questionSet.questions.length; qIndex++) {
      const question = questionSet.questions[qIndex];
      // if the question is of the type 'images'
      if (question.questionType === 'images') {
        const value = question.value;

        // if the value of the question is a non-empty array
        if (value && value.length) {
          // iterate over each element of the value array. each element represents an image
          // a remote image (in the case of an update) will be an id string
          for (const file of value) {
            if (typeof file !== 'string') {
              imageUploads.push({
                sport,
                areaId,
                formResponseId: formResponse.id,
                questionSetIndex: qsIndex,
                questionIndex: qIndex,
                file: file,
              });
            }
          }
        }
      }
    }
  }

  /**
   * Submit the form response
   */
  let ullrApiUploadMethod = putFormResponse;
  let requestId: string | null = formResponse.id;
  if (createOrUpdate === CONSTANTS.CREATE_OR_UPDATE.CREATE) {
    ullrApiUploadMethod = putFormResponse;
  } else if (createOrUpdate === CONSTANTS.CREATE_OR_UPDATE.UPDATE) {
    ullrApiUploadMethod = postFormResponse;
    requestId = null;
  }
  const savedFormResponse = await ullrApiUploadMethod(
    {
      sport,
      areaId,
      formResponse,
    },
    { requestId },
  );

  /**
   * Upload each image
   */
  for (let imageIndex = 0; imageIndex < imageUploads.length; imageIndex++) {
    const imageUpload = imageUploads[imageIndex];
    await putFormResponseImage(
      {
        ...imageUpload,
        formResponseId: savedFormResponse.id,
      },
      { requestId: `${imageUpload.formResponseId}-${imageUpload.questionSetIndex}-${imageUpload.questionIndex}-${imageIndex}` },
    );
  }

  return getFormResponse({
    sport,
    areaId,
    formResponseId: savedFormResponse.id,
  });
};

const deleteFormResponse = ({sport, areaId, formResponseId}: DeleteFormResponseParams): Promise<any> => {
  const url = `/${sport}/areas/${areaId}/formresponses/${formResponseId}`;
  return axios.delete(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getFormResponsesExportReportSummary = ({sport, areaId, startDate, endDate, fileName}: GetFormResponsesExportReportSummaryParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/formresponses/exportreportsummary`;
  // add query parameters
  const queryParameters: string[] = [];
  if (startDate && endDate) {
    queryParameters.push(`startDate=${startDate}`);
    queryParameters.push(`endDate=${endDate}`);
  }
  if (queryParameters && queryParameters.length) {
    url += `?${queryParameters.join('&')}`;
  }
  return axios.get(url, {
    responseType: 'arraybuffer',
    headers: {
      'Accept': 'application/zip'
    }
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.csv`); //or any other extension
      document.body.appendChild(link);
      link.click();
    });
};

const getFormResponsesCount = ({sport, areaId, day, startDate, endDate, formTypes, excludeFormTypes, formCategories, featureId, trailId, spatialFeatureId}: GetFormResponsesCountParams): Promise<any> => {
  let url = `/${sport}/areas/${areaId}/formresponses/count`;
  // add query parameters
  const queryParameters: string[] = [];
  if (day) {
    queryParameters.push(`day=${day}`);
  } else if (startDate && endDate) {
    queryParameters.push(`startDate=${startDate}`);
    queryParameters.push(`endDate=${endDate}`);
  }
  if (formTypes) {
    queryParameters.push(`formTypes=${Array.isArray(formTypes) ? formTypes.join(',') : formTypes}`);
  }
  if (excludeFormTypes) {
    queryParameters.push(`excludeFormTypes=${Array.isArray(excludeFormTypes) ? excludeFormTypes.join(',') : excludeFormTypes}`);
  }
  if (formCategories) {
    queryParameters.push(`formCategories=${Array.isArray(formCategories) ? formCategories.join(',') : formCategories}`);
  }
  if (featureId) {
    queryParameters.push(`featureId=${featureId}`);
  }
  if (trailId) {
    queryParameters.push(`trailId=${trailId}`);
  }
  if (spatialFeatureId) {
    queryParameters.push(`spatialFeatureId=${spatialFeatureId}`);
  }
  if (queryParameters && queryParameters.length) {
    url += `?${queryParameters.join('&')}`;
  }
  return axios.get(url)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
    });
};

const formResponseFunctions = {
  getFormResponses,
  getFormResponsesExport,
  getFormResponseExport,
  getFormResponsesExportReportSummary,
  getFormResponse,
  getCsv,
  getJumpMeasurementExport,
  orchestrateUpload,
  deleteFormResponse,
  getFormResponsesCount,
};

export default formResponseFunctions;
